import { Alert, message } from 'antd'
import BAPLIE_SYNC_STATUSES, {
  BAPLIE_SYNC_STATUS_KEYS,
} from 'constants/baplieSyncStatuses'
import React, { useState } from 'react'
import BAPLIE_FILE_TYPES from 'constants/baplieFileTypes'
import { formatBackendDateTimeNaive } from 'global/util/time'
import getDeviceSyncStatusIcon from './getDeviceSyncStatusIcon'
import HistoryList from './HistoryList'
import NavigationTab from 'components/NavigationTabs'
import PropTypes from 'prop-types'
import Text from 'components/Text'
import { useTranslation } from 'react-i18next'

import './baplieOrRestowHistory.less'

const BaplieOrRestowHistory = ({ baplieData, isDownloading, onDownload }) => {
  const { t } = useTranslation()
  const [activeTab, setActiveTab] = useState('all')

  const fileTypeLabels = {
    [BAPLIE_FILE_TYPES.DISCHARGE]: t('portCall.shipOverview.discharge'),
    [BAPLIE_FILE_TYPES.LOAD]: t('portCall.shipOverview.load'),
    [BAPLIE_FILE_TYPES.RESTOW]: t('portCall.shipOverview.restow'),
  }

  const tabs = [
    {
      key: 'all',
      label: t('portCall.shipOverview.all'),
    },
    {
      key: BAPLIE_FILE_TYPES.DISCHARGE,
      label: t('portCall.shipOverview.discharge'),
    },
    {
      key: BAPLIE_FILE_TYPES.LOAD,
      label: t('portCall.shipOverview.load'),
    },
    {
      key: BAPLIE_FILE_TYPES.RESTOW,
      label: t('portCall.shipOverview.restow'),
    },
  ]

  const filteredData =
    activeTab === 'all'
      ? baplieData || []
      : baplieData?.filter(item => item.type === activeTab) || []

  // Sort by uploadedTime in descending order (newest first)
  const sortedData = [...filteredData].sort(
    (a, b) => new Date(b.uploadedTime) - new Date(a.uploadedTime)
  )

  const handleDownload = (fileId, filename, type) => {
    if (isDownloading) {
      return
    }

    if (!onDownload) {
      message.error(
        t('portCall.shipOverview.baplieOrRestowInfo.downloadFailed')
      )

      return
    }

    onDownload({
      fileId,
      filename,
      onError: () =>
        message.error(
          t('portCall.shipOverview.baplieOrRestowInfo.downloadFailed')
        ),
      onSuccess: () =>
        message.success(
          t('portCall.shipOverview.baplieOrRestowInfo.downloadSuccess')
        ),
      type,
    })
  }

  const renderDeviceStatus = devices => {
    if (!devices || devices.length === 0) {
      return (
        <Text className="baplie-or-restow-history__no-devices">
          {t('portCall.shipOverview.noDeviceStatus')}
        </Text>
      )
    }

    return (
      <div className="baplie-or-restow-history__devices">
        {devices.map(device => {
          const statusKey = BAPLIE_SYNC_STATUS_KEYS[device.syncStatus]
          const statusLabel = statusKey
            ? t(statusKey)
            : t('portCall.shipOverview.baplieOrRestowInfo.unknown')
          const statusClass = statusLabel.toLowerCase().replace(/\s+/g, '-')
          const syncAtFormatted = device.syncAt
            ? formatBackendDateTimeNaive(device.syncAt)
            : ''

          const StatusIcon = getDeviceSyncStatusIcon(device.syncStatus)

          return (
            <div
              className="baplie-or-restow-history__device"
              key={device.deviceid}
            >
              <Text className="baplie-or-restow-history__device-name">
                {device.deviceName}:
              </Text>
              <div
                className={`baplie-or-restow-history__device-status-badge baplie-or-restow-history__device-status-badge--${statusClass}`}
              >
                {StatusIcon && (
                  <StatusIcon className="baplie-or-restow-history__device-status-icon" />
                )}
                <Text className="baplie-or-restow-history__device-status-text">
                  {statusLabel}
                </Text>
              </div>
              {syncAtFormatted && (
                <>
                  <span className="baplie-or-restow-history__device-divider">
                    |
                  </span>
                  <Text className="baplie-or-restow-history__device-sync-time">
                    {syncAtFormatted}
                  </Text>
                </>
              )}
            </div>
          )
        })}
      </div>
    )
  }

  const renderHistoryList = () => {
    if (sortedData.length === 0) {
      return (
        <div className="baplie-or-restow-history__empty">
          <Text>
            {t('portCall.shipOverview.baplieOrRestowInfo.noFilesUploadedYet')}
          </Text>
        </div>
      )
    }

    return (
      <HistoryList
        downloadLabel={t('portCall.shipOverview.baplieOrRestowInfo.download')}
        fileTypeLabels={activeTab === 'all' ? fileTypeLabels : null}
        items={sortedData}
        onDownload={handleDownload}
        renderDeviceStatus={renderDeviceStatus}
      />
    )
  }

  return (
    <div className="baplie-or-restow-history">
      <Alert
        className="baplie-or-restow-history__note"
        type="info"
        message={t(
          'portCall.shipOverview.baplieOrRestowInfo.historyDashboardNote'
        )}
      />
      <NavigationTab
        activeKey={activeTab}
        className="baplie-or-restow-history__tabs"
        items={tabs}
        onTabChange={setActiveTab}
      />

      {renderHistoryList()}
    </div>
  )
}

BaplieOrRestowHistory.defaultProps = {
  baplieData: [],
  isDownloading: false,
  onDownload: null,
}

BaplieOrRestowHistory.propTypes = {
  baplieData: PropTypes.arrayOf(
    PropTypes.shape({
      devices: PropTypes.arrayOf(
        PropTypes.shape({
          deviceName: PropTypes.string.isRequired,
          deviceid: PropTypes.string.isRequired,
          syncAt: PropTypes.string,
          syncStatus: PropTypes.oneOf(Object.values(BAPLIE_SYNC_STATUSES))
            .isRequired,
        })
      ).isRequired,
      filename: PropTypes.string.isRequired,
      id: PropTypes.string.isRequired,
      type: PropTypes.oneOf(Object.values(BAPLIE_FILE_TYPES)).isRequired,
      uploadedTime: PropTypes.string.isRequired,
    })
  ),
  isDownloading: PropTypes.bool,
  onDownload: PropTypes.func,
}

export default BaplieOrRestowHistory
